import * as d3 from 'd3';
import $ from 'jquery';
import * as React from 'react';
import { observer } from 'mobx-react';
import { ShotViewerHelper, UnitSystem, Unit, Parameter } from 'MeasurementsCalculations';
import { IOptimizedTrajectoryBackgroundParams } from './IOptimizedTrajectoryBackgroundParams';
import { ITrajectoryCoordinate } from 'Data';
import { OptimizedTrajectoryAxis } from './../OptimizedTrajectoryAxis/OptimizedTrajectoryAxis';
import { OptimizedSingleTrajectory } from './../OptimizedSingleTrajectory/OptimizedSingleTrajectory';
import { OptimizedTrajectoryArea } from './../OptimizedTrajectoryArea/OptimizedTrajectoryArea';

@observer
export class OptimizedTrajectoryBackground extends React.Component<
  IOptimizedTrajectoryBackgroundParams
> {
  private theLongestStroke: number = 0;
  private theHighestStroke: number = 0;
  private strokeMaxHeight: number = 0;
  private strokeMaxLength: number = 0;
  private xAxis: d3.svg.Axis | undefined;
  private yAxis: d3.svg.Axis | undefined;
  private x: any;
  private y: any;
  private sideViewWidth: number = 0;
  private sideViewHeight: number = 0;
  private margins = { top: 20, bottom: 20, left: 0, right: 30 };
  private joinedTrajectory: Array<ITrajectoryCoordinate> = [];
  private maxDisplayLength = 0;
  private maxDisplayHeight = 0;
  private trajectoryCoordinates: Array<ITrajectoryCoordinate> = [];
  private aspectRatio = 0;

  constructor(props: IOptimizedTrajectoryBackgroundParams) {
    super(props, undefined);
    this.fillOutValuesFromProps(props);
  }

  componentDidMount(): void {
    if (OptimizedTrajectoryBackground.isBackgroundLoading) return;

    this.loadBackground();
  }

  componentDidUpdate() {
    if (OptimizedTrajectoryBackground.isBackgroundLoading) return;

    this.loadBackground();
  }

  loadBackground() {
    if (OptimizedTrajectoryBackground.isBackgroundLoading) {
      setTimeout(() => this.loadBackground());
      return;
    }

    if (OptimizedTrajectoryBackground.backgroundSvg) {
      setTimeout(() => this.setBackground(OptimizedTrajectoryBackground.backgroundSvg));
      return;
    }

    OptimizedTrajectoryBackground.isBackgroundLoading = true;
    d3.xml('Images/bckg-test-5-corrected.svg', d => {
      OptimizedTrajectoryBackground.isBackgroundLoading = false;
      OptimizedTrajectoryBackground.backgroundSvg = $(d.documentElement).children();
      this.setBackground(OptimizedTrajectoryBackground.backgroundSvg);
    });
  }
  private setBackground(backgroundSvg: any): void {
    $('.background-wrapper').empty();
    $('.background-wrapper').append(backgroundSvg);
  }

  private static isBackgroundLoading: boolean = false;
  private static backgroundSvg: any = null;

  fillOutValuesFromProps(props: IOptimizedTrajectoryBackgroundParams) {
    if (
      (props.selectedStroke != null &&
        props.selectedStroke.trajectory != null &&
        props.selectedStroke.trajectory.length !== 0) ||
      (props.selectedAverage != null &&
        props.selectedAverage.trajectory != null &&
        props.selectedAverage.trajectory.length !== 0)
    ) {
      if (props.selectedStroke) {
        this.trajectoryCoordinates = props.selectedStroke.trajectory ?? [];
      } else {
        this.trajectoryCoordinates = props.selectedAverage?.trajectory ?? [];
      }
      this.createJoinedUpperAndLowerTrajectory(props);
    }

    this.sideViewWidth = 1140 - (this.margins.left + this.margins.right);
    this.sideViewHeight =
      this.sideViewWidth * (502 / 1366) - (this.margins.left + this.margins.right);
    this.aspectRatio = this.sideViewHeight / this.sideViewWidth;

    this.setStrokeMaxValues(this.trajectoryCoordinates, this.joinedTrajectory);

    this.maxDisplayLength = ShotViewerHelper.displayMaxLength(this.strokeMaxLength);
    this.maxDisplayHeight = this.maxDisplayLength * this.aspectRatio;
    //if height of the stroke is lower than the graph height, recalculate the graph dimentions based on height
    if (this.maxDisplayHeight < this.strokeMaxHeight) {
      this.maxDisplayHeight = ShotViewerHelper.displayMaxHeight(this.strokeMaxHeight);
      this.maxDisplayLength = this.maxDisplayHeight / this.aspectRatio;
    }
    this.x = d3.scale.linear().range([0, this.sideViewWidth]);
    this.x.domain([0, this.maxDisplayLength]);
    this.y = d3.scale.linear().range([this.sideViewHeight, 0]);
    this.y.domain([0, this.maxDisplayHeight]);
    this.maxDisplayHeight = this.maxDisplayLength * this.aspectRatio;
    this.maxDisplayLength = this.maxDisplayHeight / this.aspectRatio;

    this.calculateAxisValues(
      props,
      this.sideViewWidth,
      this.sideViewHeight,
      this.maxDisplayLength,
      this.maxDisplayHeight
    );
  }

  createJoinedUpperAndLowerTrajectory(props: IOptimizedTrajectoryBackgroundParams) {
    if (props.optimizedData == null) return;

    if (props.optimizedData.upperTrajectory && props.optimizedData.lowerTrajectory) {
      this.joinedTrajectory = [];
      for (var i = 0; i < props.optimizedData.lowerTrajectory.length; i++) {
        this.joinedTrajectory.push(props.optimizedData.lowerTrajectory[i]);
      }
      for (i = props.optimizedData.upperTrajectory.length - 1; i >= 0; i--) {
        this.joinedTrajectory.push(props.optimizedData.upperTrajectory[i]);
      }
    }
  }

  setStrokeMaxValues(
    coordinates: Array<ITrajectoryCoordinate>,
    coordinatesForJoinedTrajectory: Array<ITrajectoryCoordinate>
  ) {
    let maxHeight: number = 0;
    let maxLength: number = 0;
    this.strokeMaxLength = 0;
    this.strokeMaxHeight = 0;
    if (
      coordinates != null &&
      coordinates.length !== 0 &&
      coordinatesForJoinedTrajectory != null && coordinatesForJoinedTrajectory.length !== 0
    ) {
      let maxHeightOfNormalizedArea = d3.max(coordinatesForJoinedTrajectory, d => {
        return d.y;
      });
      let maxLengthOfNormalizedArea = d3.max(coordinatesForJoinedTrajectory, d => {
        return d.x;
      });
      let maxLengthOfStroke = d3.max(coordinates, d => {
        return d.x;
      });
      let maxHeightOfStroke = d3.max(coordinates, d => {
        return d.y;
      });
      if (maxHeightOfNormalizedArea >= maxHeightOfStroke) {
        maxHeight = maxHeightOfNormalizedArea;
      } else {
        maxHeight = maxHeightOfStroke;
      }
      if (maxLengthOfNormalizedArea >= maxLengthOfStroke) {
        maxLength = maxLengthOfNormalizedArea;
      } else {
        maxLength = maxLengthOfStroke;
      }
    }
    if (this.strokeMaxLength < Math.abs(maxLength)) {
      this.strokeMaxLength = Math.abs(maxLength);
    }
    if (this.strokeMaxHeight < Math.abs(maxHeight)) {
      this.strokeMaxHeight = Math.abs(maxHeight);
    }
    if (this.strokeMaxLength === 0 && this.strokeMaxHeight === 0) {
      this.strokeMaxLength = 150;
      this.strokeMaxHeight = 30;
    }
    //Defaut value for graph height if it is below 40 meters
    if (this.strokeMaxHeight < 40) {
      this.strokeMaxHeight = 40;
    }
  }

  calculateAxisValues(
    props: IOptimizedTrajectoryBackgroundParams,
    sideViewWidth: number,
    sideViewHeight: number,
    maxDisplayLength: number,
    maxDisplayHeight: number
  ) {
    //tick values for European Unit System
    if (
      props.unitPresentation.unitSystem === UnitSystem.Metric ||
      props.unitPresentation.unitSystem === UnitSystem.European
    ) {
      this.xAxis = d3.svg
        .axis()
        .scale(this.x)
        .orient('bottom')
        .tickSize(-this.sideViewHeight)
        .ticks(6);
      this.yAxis = d3.svg
        .axis()
        .scale(this.y)
        .orient('right')
        .tickSize(-this.sideViewWidth)
        .ticks(4);
    }
    //tick values for Us Unit System
    else if (props.unitPresentation.unitSystem === UnitSystem.Us) {
      let xUs = d3.scale.linear().range([0, sideViewWidth]);
      let yUs = d3.scale.linear().range([sideViewHeight, 0]);
      xUs.domain([0, this.props.unitPresentation.convert(Unit.Yard, maxDisplayLength)]);
      yUs.domain([0, this.props.unitPresentation.convert(Unit.Foot, maxDisplayHeight)]);
      this.xAxis = d3.svg
        .axis()
        .scale(xUs)
        .orient('bottom')
        .tickSize(-this.sideViewHeight)
        .ticks(6);
      this.yAxis = d3.svg
        .axis()
        .scale(yUs)
        .orient('right')
        .tickSize(-this.sideViewWidth)
        .ticks(4);
    }
    //ticks values for Korean and Japanese
    else if (
      props.unitPresentation.unitSystem === UnitSystem.Korean ||
      props.unitPresentation.unitSystem === UnitSystem.Japanese
    ) {
      let xJapaneseAndKorean = d3.scale.linear().range([0, sideViewWidth]);
      let yJapaneseAndKorean = d3.scale.linear().range([sideViewHeight, 0]);
      xJapaneseAndKorean.domain([
        0,
        this.props.unitPresentation.convert(Unit.Yard, maxDisplayLength),
      ]);
      yJapaneseAndKorean.domain([
        0,
        this.props.unitPresentation.convert(Unit.Yard, maxDisplayHeight),
      ]);
      this.xAxis = d3.svg
        .axis()
        .scale(xJapaneseAndKorean)
        .orient('bottom')
        .tickSize(-this.sideViewHeight)
        .ticks(6);
      this.yAxis = d3.svg
        .axis()
        .scale(yJapaneseAndKorean)
        .orient('right')
        .tickSize(-this.sideViewWidth)
        .ticks(4);
    }
  }

  render() {
    let self = this;
    //define viewport
    let viewportWidth = this.sideViewWidth + this.margins.left + this.margins.right;
    let viewportHeight = this.sideViewHeight + this.margins.top + this.margins.bottom;
    let viewPortString = '0 0 ' + viewportWidth.toString() + ' ' + viewportHeight.toString();

    //background
    let fieldLength = 480;
    let backgroundWidth = 1417;
    let backgroundHeight = 507;
    let scalingXForRectGroup =
      (this.sideViewWidth / backgroundWidth) * (fieldLength / this.maxDisplayLength);
    let scalingYForRectGroup =
      (this.sideViewHeight / backgroundHeight) * (fieldLength / this.maxDisplayLength);
    let translatingYForRectGroup =
      this.sideViewHeight - (fieldLength / this.maxDisplayLength) * this.sideViewHeight;

    return (
      <div className="OptimizedTrajectoryBackground">
        <svg
          width={viewportWidth}
          height={viewportHeight}
          viewBox={viewPortString}
          preserveAspectRatio="xMinYMid"
          id="sideViewCanvas"
        >
          <g
            id="svgWithTrajectories"
            transform={'translate(' + this.margins.left + ',' + this.margins.top + ')'}
          >
            <svg
              className="rect-background"
              width={this.sideViewWidth}
              height={this.sideViewHeight}
            >
              <g
                className="background-wrapper"
                transform={
                  'translate(0,' +
                  translatingYForRectGroup +
                  ') scale(' +
                  scalingXForRectGroup +
                  ', ' +
                  scalingYForRectGroup +
                  ')'
                }
              ></g>
            </svg>
            <OptimizedTrajectoryAxis
              className="TrajectoryAxis"
              axis={this.xAxis}
              scale={this.x}
              tickSize={this.sideViewHeight}
              orient="bottom"
              translate={`translate(0, ${this.sideViewHeight})`}
            />
            <text
              className="legend"
              y={this.sideViewHeight - this.margins.bottom - 3}
              x={this.sideViewWidth + 4}
              dy="1.5em"
            >
              {this.props.unitPresentation.displayUnit(Parameter.Height)}
            </text>
            <OptimizedTrajectoryAxis
              className="TrajectoryAxis"
              axis={this.yAxis}
              scale={this.y}
              tickSize={this.sideViewWidth}
              orient="right"
              translate={`translate(${this.sideViewWidth},0)`}
            />
            <text
              className="legend"
              dx="0.2em"
              dy="0.9em"
              transform={`translate(0, ${this.sideViewHeight})`}
            >
              {this.props.unitPresentation.displayUnit(Parameter.Total)}
            </text>
            {this.trajectoryCoordinates && this.joinedTrajectory && (
              <OptimizedTrajectoryArea
                className="line-upper-optimizer OptimizedTrajectoryArea"
                x={self.x}
                y={self.y}
                trajectoryColor="#367FC0"
                coordinates={this.joinedTrajectory}
              />
            )}
            {this.trajectoryCoordinates && this.joinedTrajectory && (
              <OptimizedSingleTrajectory
                className="trajectory-line-no-hover-effect OptimizedSingleTrajectory"
                x={self.x}
                y={self.y}
                trajectoryColor="#EC691A"
                coordinates={this.trajectoryCoordinates}
              />
            )}
          </g>
        </svg>
      </div>
    );
  }
}
